import type { CaptureResult } from "posthog-js";
import { stripLocaleFromPathname } from "@/lib/i18n/locale";
import { SITE_URL } from "@/lib/site";

// Public page names only. Unknown paths must not leak personal URL segments.
const publicPaths = new Set([
	"/",
	"/contact",
	"/a-propos",
	"/produits",
	"/services/applications-metier",
	"/services/migration-react-native-expo",
	"/cas-usage",
	"/cas-usage/cabinet-qhse",
	"/cas-usage/controle-qualite-proprete",
	"/cas-usage/maintenance-cvc",
	"/cas-usage/reprise-application-react-native",
	"/realisations",
	"/realisations/chouic",
	"/realisations/questovery",
	"/realisations/vaerdict",
	"/blog",
	"/blog/hello-world",
	"/docs",
	"/docs/legal",
	"/docs/privacy",
]);

export const limitedAnalyticsEvents = new Set([
	"$pageview",
	"cta_click",
	"contact_submit",
]);

export function getAnalyticsPath(pathname: string): string | undefined {
	const path = stripLocaleFromPathname(pathname).replace(/\/+$/, "") || "/";
	return publicPaths.has(path) ? path : undefined;
}

export function getReferrerHost(referrer: string): string | undefined {
	try {
		const url = new URL(referrer);
		return /^https?:$/.test(url.protocol) ? url.hostname : undefined;
	} catch {
		return undefined;
	}
}

const safeLabel = (value: unknown) =>
	typeof value === "string" && /^[a-zA-Z0-9_-]{1,64}$/.test(value)
		? value
		: undefined;

/** Enforce the same policy for calls made by components and by the SDK. */
export function filterAnalyticsEvent(
	event: CaptureResult | null,
): CaptureResult | null {
	if (!event || !limitedAnalyticsEvents.has(event.event)) return null;
	const source = event.properties;
	if (
		event.event === "cta_click" &&
		!["contact", "booking"].includes(source.cta)
	)
		return null;
	const properties: Record<string, unknown> = {};
	// Preserve the ingestion token and pseudonymous identifiers, not profiles.
	for (const key of [
		"token",
		"$lib",
		"$lib_version",
		"$browser",
		"$os",
		"$device_type",
	]) {
		if (typeof source[key] === "string") properties[key] = source[key];
	}
	for (const key of [
		"distinct_id",
		"$device_id",
		"$session_id",
		"$window_id",
	]) {
		if (typeof source[key] === "string" && /^[a-f0-9-]{36}$/i.test(source[key]))
			properties[key] = source[key];
	}
	if (!properties.distinct_id) return null;
	properties.$process_person_profile = false;
	if (["fr-FR", "en-US"].includes(source.locale))
		properties.locale = source.locale;
	const path = getAnalyticsPath(
		typeof source.path === "string" ? source.path : window.location.pathname,
	);
	if (!path) return null;
	properties.path = path;
	properties.$current_url = `${SITE_URL}${path}`;
	if (event.event === "$pageview") {
		const host = getReferrerHost(document.referrer);
		if (host) {
			properties.$referring_domain = host;
			properties.$referrer = `https://${host}/`;
		}
	}
	if (event.event === "cta_click") {
		properties.cta = source.cta;
		const placement = safeLabel(source.placement);
		if (placement) properties.placement = placement;
	}
	if (
		event.event === "contact_submit" &&
		["success", "failure"].includes(source.status)
	)
		properties.status = source.status;
	// $set/$set_once and unknown top-level fields are intentionally discarded.
	return {
		uuid: event.uuid,
		event: event.event,
		timestamp: event.timestamp,
		properties,
	};
}
